import { httpService } from './httpService';
import { API_BASE_URL, REQ_ENC_KEY } from './api';
import { decryptResponse, encryptResponse } from './encrypt';
import { getSessionKey } from './sessionKey';

export type InstructionMode = 'paragraph' | 'points' | 'both';

export const resolveInstructionMode = (paragraph: string, points: string[]): InstructionMode => {
  const hasParagraph = Boolean(paragraph.trim());
  const hasPoints = points.some((point) => Boolean(point.trim()));
  if (hasParagraph && hasPoints) return 'both';
  if (hasPoints) return 'points';
  return 'paragraph';
};

export const buildInstructionsPayload = (
  paragraph: string,
  points: string[]
): Omit<KnowledgeInstructions, 'updated_at'> => {
  const cleanParagraph = paragraph.trim();
  const cleanPoints = points.map((point) => point.trim()).filter(Boolean);
  return {
    mode: resolveInstructionMode(cleanParagraph, cleanPoints),
    paragraph: cleanParagraph,
    points: cleanPoints,
  };
};

export interface KnowledgeInstructions {
  mode: InstructionMode;
  paragraph: string;
  points: string[];
  updated_at?: string | null;
}

export interface KnowledgeFile {
  file_id: string;
  original_filename: string;
  file_size: number;
  file_type: string;
  mime_type: string;
  processing_status: string;
  created_at?: string;
}

export interface KnowledgeText {
  text_id: string;
  title: string;
  content: string;
  created_at?: string;
  updated_at?: string;
}

export interface KnowledgeLink {
  link_id: string;
  url: string;
  processing_status: string;
  created_at?: string;
}

export interface KnowledgeBase {
  bot_id: string;
  status: string;
  instructions: KnowledgeInstructions;
  files: KnowledgeFile[];
  texts: KnowledgeText[];
  links: KnowledgeLink[];
  counts: { files: number; texts: number; links: number };
  limits: { files: number; texts: number; links: number };
  updated_at?: string | null;
}

export interface BotContext {
  organization_id: string;
  application_id: string;
  bot_id: string;
}

interface ApiEnvelope {
  status: number;
  message?: string;
  data?: string;
}

interface ApiResult<T> {
  message: string;
  data: T;
}

export const getKnowledgeApiError = (error: any, fallback: string): string =>
  error?.response?.data?.error?.errorDetails
  || error?.response?.data?.message
  || error?.message
  || fallback;

const unwrap = async <T>(response: { data: ApiEnvelope }, fallback: string): Promise<ApiResult<T>> => {
  if (response.data?.status !== 200 || !response.data.data) {
    throw new Error(response.data?.message || fallback);
  }
  const data = await decryptResponse<T>(response.data.data, getSessionKey() as string);
  return { message: response.data.message || '', data };
};

const post = async <T>(
  endpoint: string,
  ctx: BotContext,
  fallback: string,
  extra: Record<string, unknown> = {}
): Promise<ApiResult<T>> => {
  const payload = { data: await encryptResponse({ ...ctx, ...extra }, REQ_ENC_KEY) };
  const response = await httpService.post<ApiEnvelope>(`${API_BASE_URL}/ai/${endpoint}`, payload);
  return unwrap<T>(response, fallback);
};

type KnowledgeBaseResult = { knowledgeBase: KnowledgeBase };

export const aiChatBotKnowledgeService = {
  getKnowledgeBase: (ctx: BotContext) =>
    post<KnowledgeBaseResult>('getChatBotKnowledgeBase', ctx, 'Failed to load knowledge base.'),

  saveInstructions: (ctx: BotContext, instructions: Omit<KnowledgeInstructions, 'updated_at'>) =>
    post<KnowledgeBaseResult>('saveChatBotInstructions', ctx, 'Failed to save instructions.', instructions),

  uploadFiles: async (ctx: BotContext, files: File[]) => {
    const formData = new FormData();
    formData.append('data', await encryptResponse(ctx, REQ_ENC_KEY));
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });
    const response = await httpService.post<ApiEnvelope>(
      `${API_BASE_URL}/ai/uploadChatBotKnowledgeFiles`,
      formData
    );
    return unwrap<KnowledgeBaseResult & { uploaded: KnowledgeFile[] }>(response, 'Failed to upload files.');
  },

  getFileUrl: (ctx: BotContext, fileId: string, disposition: 'inline' | 'attachment') =>
    post<{ file: KnowledgeFile; url: string; expires_in: number }>(
      'getChatBotKnowledgeFileUrl',
      ctx,
      'Failed to open file.',
      { file_id: fileId, disposition }
    ),

  deleteFile: (ctx: BotContext, fileId: string) =>
    post<KnowledgeBaseResult>('deleteChatBotKnowledgeFile', ctx, 'Failed to delete file.', { file_id: fileId }),

  addText: (ctx: BotContext, title: string, content: string) =>
    post<KnowledgeBaseResult & { text: KnowledgeText }>(
      'addChatBotKnowledgeText',
      ctx,
      'Failed to add text.',
      { title, content }
    ),

  updateText: (ctx: BotContext, textId: string, title: string, content: string) =>
    post<KnowledgeBaseResult>(
      'updateChatBotKnowledgeText',
      ctx,
      'Failed to update text.',
      { text_id: textId, title, content }
    ),

  deleteText: (ctx: BotContext, textId: string) =>
    post<KnowledgeBaseResult>('deleteChatBotKnowledgeText', ctx, 'Failed to delete text.', { text_id: textId }),

  addLink: (ctx: BotContext, url: string) =>
    post<KnowledgeBaseResult & { link: KnowledgeLink }>('addChatBotKnowledgeLink', ctx, 'Failed to add link.', { url }),

  deleteLink: (ctx: BotContext, linkId: string) =>
    post<KnowledgeBaseResult>('deleteChatBotKnowledgeLink', ctx, 'Failed to delete link.', { link_id: linkId }),

  activate: (ctx: BotContext, instructions?: Omit<KnowledgeInstructions, 'updated_at'>) =>
    post<KnowledgeBaseResult & { chatBot: any }>(
      'activateAiChatBot',
      ctx,
      'Failed to activate AI chat bot.',
      instructions ? { instructions } : {}
    ),
};
